import type { WheelOutcome, WheelSegment } from './types';

/**
 * Outcome of resolving a wheel spin against a bet.
 *
 * <p>cs-core's domain emitted three different event classes
 * ({@code WheelOfFortuneWinEvent}, {@code BankruptEvent}, {@code FreeSpinsEvent});
 * here we collapse to a single record carrying enough information for the host
 * scene to react (payout / bankruptcy / free-spin grant).
 */
export interface WheelSettlement {
  bet: number;
  segment: WheelSegment;
  /** Coins the player wins from this spin. {@code 0} for bankrupt / 0× /
   * free-spin segments (free-spin awards aren't coin-denominated). */
  winnings: number;
  /** True when the wedge is a bankrupt — host should react by zeroing the
   * relevant balance / round-bank. */
  isBankrupt: boolean;
  /** Number of free spins awarded by this segment. {@code 0} if not a
   * free-spin segment. */
  freeSpinsAwarded: number;
}

export function settleWheel(outcome: WheelOutcome, bet: number): WheelSettlement {
  const segment = outcome.segment;
  switch (segment.kind) {
    case 'multiplier':
      return {
        bet,
        segment,
        winnings: bet * segment.multiplier,
        isBankrupt: false,
        freeSpinsAwarded: 0,
      };
    case 'bankrupt':
      return {
        bet,
        segment,
        winnings: 0,
        isBankrupt: true,
        freeSpinsAwarded: 0,
      };
    case 'free-spin':
      return {
        bet,
        segment,
        winnings: 0,
        isBankrupt: false,
        freeSpinsAwarded: segment.freeSpins,
      };
  }
}
